import { format } from "date-fns"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import UsageHistoryStrip from "@/v2-backoffice-client/usage/UsageHistoryStrip"
import { formatTrendDetail } from "@/v2-backoffice-client/usage/usage.constants"
import UsageReportBarList from "@/v2-backoffice-client/usage/report/UsageReportBarList"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import UsageReportKpi from "@/v2-backoffice-client/usage/report/UsageReportKpi"
import UsageReportNotes from "@/v2-backoffice-client/usage/report/UsageReportNotes"
import UsageReportProse from "@/v2-backoffice-client/usage/report/UsageReportProse"
import UsageReportSection from "@/v2-backoffice-client/usage/report/UsageReportSection"
import { useUsageMcpCustomerQuery } from "@/v2-backoffice-client/usage/mcp/usage.mcpCustomer.query"
import { useUsageMcpUserQuery } from "@/v2-backoffice-client/usage/mcp/usage.mcpUser.query"
import { formatLicenseDeniedReason } from "@/v2-backoffice-client/usage/denied/usage.denied.constants"
import {
  formatMcpClient,
  formatMcpTool,
  MCP_CLIENT_LIST_LIMIT,
  MCP_HISTORY_DAYS,
  MCP_REASON_LIST_LIMIT,
  MCP_REPORT_NOTES,
  MCP_TOOL_LIST_LIMIT,
} from "@/v2-backoffice-client/usage/mcp/usage.mcp.constants"
import { deriveMcpCustomerStory } from "@/v2-backoffice-client/usage/mcp/usage.mcp.story"
import {
  deriveMcpHistory,
  deriveMcpToolGroups,
  deriveMcpWindow,
  hasMcpActivity,
  mergeMcpCounts,
  type UsageMcpCountRow,
  type UsageMcpDayRow,
} from "@/v2-backoffice-client/usage/mcp/usage.mcp.utils"
import UsageMcpMembersTable, {
  type UsageMcpMember,
} from "@/v2-backoffice-client/usage/mcp/UsageMcpMembersTable"
import UsageMcpWeekChart from "@/v2-backoffice-client/usage/mcp/UsageMcpWeekChart"
import { usageKpiGridClass } from "@/v2-backoffice-client/usage/report/usage.report.utils"

export type UsageMcpReportProps = {
  /** Scope to a customer's active users. Exactly one scope prop is set. */
  customerId?: number
  /** Scope to one user. */
  userId?: number
  /** The server this page is about ('mcp-placepoint' | 'mcp-dataset'). */
  appCode: string
  /** Its product name as sales says it ("Placepoint MCP"). */
  productName: string
  /** The customer's or person's name, for the title and the prose. */
  subject: string
}

const EMPTY_DAYS: UsageMcpDayRow[] = []
const EMPTY_COUNTS: UsageMcpCountRow[] = []
const EMPTY_MEMBERS: UsageMcpMember[] = []

/** What each server is for, in one sentence, for the lead and the empty state. */
const PRODUCT_LEADS: Record<string, string> = {
  "mcp-placepoint":
    "Property, company, plan and map data answered inside an AI client such as Claude, ChatGPT or Copilot. One question usually fans out into several tool calls.",
  "mcp-dataset":
    "Creating and editing map datasets in a Placepoint project from an AI client — upload a file, match it to properties, style it, put it on the map.",
}

// One product's page of a customer's or person's Activity: the MCP report
// at that scope. Key figures since the first call, the story in a few
// plain paragraphs, week by week, what it is used for, the AI clients, who
// has taken it into use, and the license gate when it is in the way. Same
// tracking events as the console's, rolled up in the database
// (acc.McpUsageDaily, AIP-47); the page derives the prose and renders.
function UsageMcpReport(props: UsageMcpReportProps) {
  const { customerId, userId, appCode, productName, subject } = props

  const customerQuery = useUsageMcpCustomerQuery({
    skip: customerId == null,
    variables: {
      customerId: customerId ?? 0,
      days: MCP_HISTORY_DAYS,
      appCode,
    },
  })
  const userQuery = useUsageMcpUserQuery({
    skip: userId == null,
    variables: { userId: userId ?? 0, days: MCP_HISTORY_DAYS, appCode },
  })

  const customerScope = customerQuery.data?.getUsageMcpByCustomer
  const scope = customerScope ?? userQuery.data?.getUsageMcpByUser
  const loading = customerQuery.loading || userQuery.loading
  const error = customerQuery.error ?? userQuery.error

  const days = scope?.Days ?? EMPTY_DAYS
  const summary = customerScope?.Summary ?? null
  const weeks = customerScope?.Weeks ?? []
  const members = customerScope?.Members ?? EMPTY_MEMBERS

  const window = deriveMcpWindow(days)
  const history = deriveMcpHistory(days, MCP_HISTORY_DAYS)
  const tools = mergeMcpCounts(scope?.Tools ?? EMPTY_COUNTS)
  const clients = mergeMcpCounts(scope?.Clients ?? EMPTY_COUNTS)
  const deniedReasons = mergeMcpCounts(scope?.DeniedReasons ?? EMPTY_COUNTS)
  const groups = deriveMcpToolGroups(tools)
  // Tool calls and Turned away always; People and A working day for a scope.
  const tileCount =
    2 +
    (summary ? 1 : 0) +
    (summary?.MedianCallsPerPersonDay28d != null ? 1 : 0)
  const story = summary
    ? deriveMcpCustomerStory({
        summary,
        weeks,
        members,
        tools,
        clients,
        groups,
        productName,
      })
    : []

  if (loading && !scope) {
    return <p className="text-sm text-muted-foreground">Loading…</p>
  }

  if (!hasMcpActivity(days)) {
    return (
      <UsageReportSection
        eyebrow={productName}
        title={`${subject} and the ${productName}`}
        lead={PRODUCT_LEADS[appCode]}
      >
        <ErrorMessage error={error} />
        <p className="usage-report-empty">
          {customerId != null
            ? `Nobody at ${subject} has connected an AI client to the ${productName} yet.`
            : `${subject} has not connected an AI client to the ${productName} yet.`}
          {appCode === "mcp-placepoint" &&
            " Anyone with a seat on the customer's MCP license can connect Claude, ChatGPT or Copilot to it; the first call shows up here the next morning."}
        </p>
      </UsageReportSection>
    )
  }

  const firstDay = summary?.FirstDay ? new Date(summary.FirstDay) : null
  const callsTotal = summary ? summary.CallsTotal : window.calls28d
  const peopleLabel = (n: number) => `${n} ${n === 1 ? "person" : "people"}`

  return (
    <UsageReportSection
      eyebrow={productName}
      title={`How ${subject} uses the ${productName}`}
      lead={
        <>
          {PRODUCT_LEADS[appCode]}
          {firstDay && (
            <>
              {" "}
              The figures run from the first call on{" "}
              {format(firstDay, "d MMMM yyyy")}.
            </>
          )}
        </>
      }
    >
      <ErrorMessage error={error} />

      <div className={usageKpiGridClass(tileCount)}>
        <UsageReportKpi
          label={summary ? "Tool calls" : "Tool calls, last 28 days"}
          value={formatNumber(callsTotal)}
          sub={[
            <>
              {summary && (
                <>{formatNumber(window.calls28d)} in the last 28 days </>
              )}
              <UsageTrendBadge
                trendPct={window.trendPct}
                detail={formatTrendDetail(window.calls28d, window.callsPrev28d)}
              />
              {!summary && <>against the four weeks before</>}
            </>,
            `${formatNumber(window.calls7d)} in the last 7 days, on ${window.activeDays28d} of the last 28`,
            window.errors28d > 0
              ? `${formatNumber(window.errors28d)} failed (${window.errorPct28d ?? 0} %)`
              : "none failed",
          ]}
        />
        {summary && (
          <UsageReportKpi
            label="People"
            value={formatNumber(summary.UsersTotal)}
            sub={[
              `${formatNumber(summary.Users28d)} active in the last 28 days`,
              `${formatNumber(summary.Users7d)} in the last 7 days`,
            ]}
          />
        )}
        {summary?.MedianCallsPerPersonDay28d != null && (
          <UsageReportKpi
            label="A working day"
            value={`${formatNumber(Math.round(summary.MedianCallsPerPersonDay28d))} calls`}
            sub={[
              summary.MedianToolsPerPersonDay28d != null
                ? `across ${formatNumber(Math.round(summary.MedianToolsPerPersonDay28d))} different tools`
                : "—",
              "for a typical person on a day they use it",
            ]}
          />
        )}
        <UsageReportKpi
          label="Turned away"
          tone={window.denied28d > 0 ? "warning" : "default"}
          value={
            window.denied28d > 0 ? (
              formatNumber(window.denied28d)
            ) : (
              <span className="text-muted-foreground">0</span>
            )
          }
          sub={
            window.denied28d > 0
              ? [
                  summary && summary.DeniedUsers28d > 0
                    ? `${peopleLabel(summary.DeniedUsers28d)} without a seat, last 28 days`
                    : "attempts without a seat, last 28 days",
                  formatLicenseDeniedReason(deniedReasons[0]?.Key),
                ]
              : window.lastDeniedDay
                ? [
                    "nobody in the last 28 days",
                    <>
                      last refused <RelativeDate date={window.lastDeniedDay} />
                    </>,
                  ]
                : "nobody has been refused"
          }
        />
      </div>

      <div className="usage-report-grid usage-report-grid--two">
        {story.length > 0 && (
          <UsageReportProse title="In short" paragraphs={story} />
        )}
        {weeks.length > 0 ? (
          <UsageReportCard
            title="Week by week"
            caption="Tool calls per week, Monday to Sunday, and the people behind them. The current week is still running."
            wide={story.length === 0}
          >
            <UsageMcpWeekChart weeks={weeks} tableLimit={6} />
          </UsageReportCard>
        ) : (
          <UsageReportCard
            title={`Calls per day, last ${MCP_HISTORY_DAYS} days`}
            wide={story.length === 0}
            caption={
              window.lastCallDay ? (
                <>
                  Last call <RelativeDate date={window.lastCallDay} />.
                </>
              ) : undefined
            }
          >
            <UsageHistoryStrip entries={history} />
          </UsageReportCard>
        )}
      </div>

      {groups.length > 0 && (
        <UsageReportSection
          title="What it is used for"
          level="subsection"
          lead="Tool calls in the last 28 days, by area and by individual tool, and the AI clients they come from."
        >
          <div className={usageKpiGridClass(groups.length)}>
            {groups.map((group) => (
              <UsageReportKpi
                key={group.group}
                label={group.label}
                value={formatNumber(group.calls)}
                sub={`${Math.round(group.share * 100)} % of the calls`}
              />
            ))}
          </div>
          <div className="usage-report-grid usage-report-grid--two">
            <UsageReportCard
              title="Most used tools"
              caption="Calls in the last 28 days and the people behind each tool."
            >
              <UsageReportBarList
                rows={tools.map((tool) => ({
                  key: tool.Key,
                  label: formatMcpTool(tool.Key),
                  value: tool.Calls28d,
                  extra:
                    customerId != null ? peopleLabel(tool.Users28d) : undefined,
                }))}
                limit={MCP_TOOL_LIST_LIMIT}
                labelWidth="w-44"
                emptyText="No tool calls in the last 28 days."
              />
            </UsageReportCard>
            <UsageReportCard
              title="AI clients"
              caption={
                customerId != null
                  ? "Share of the calls from each client, and the people who use it. A person can use more than one."
                  : "Share of the calls from each client."
              }
            >
              <UsageReportBarList
                scale="total"
                rows={clients.map((client) => ({
                  key: client.Key,
                  label: formatMcpClient(client.Key),
                  value: client.Calls28d,
                  extra:
                    customerId != null
                      ? peopleLabel(client.Users28d)
                      : `${formatNumber(client.Calls28d)} calls`,
                }))}
                limit={MCP_CLIENT_LIST_LIMIT}
                labelWidth="w-44"
                emptyText="No client has called a tool in the last 28 days."
              />
            </UsageReportCard>
          </div>
        </UsageReportSection>
      )}

      {customerId != null && members.length > 0 && (
        <UsageReportSection
          title="Who has taken it into use"
          level="subsection"
          lead="One row per person: calls in the last 28 days and the trend, days with a call, the AI clients, the top tool — and, for people without a seat, the refused attempts. Most calls first, then most refusals, so the people to sell a seat to sit right under the people already using one."
        >
          <UsageReportCard>
            <UsageMcpMembersTable members={members} />
          </UsageReportCard>
        </UsageReportSection>
      )}

      {window.denied28d > 0 && deniedReasons.length > 0 && (
        <UsageReportSection
          title="Turned away at the door"
          level="subsection"
          lead="Attempts to use it without a seat in the last 28 days, by the reason the license gate gave. AI clients retry on their own, so attempts measure persistence, not questions."
        >
          <UsageReportCard tone="warning">
            <UsageReportBarList
              tone="warning"
              rows={deniedReasons.map((reason) => ({
                key: reason.Key,
                label: formatLicenseDeniedReason(reason.Key),
                value: reason.Calls28d,
                extra:
                  customerId != null ? peopleLabel(reason.Users28d) : undefined,
              }))}
              limit={MCP_REASON_LIST_LIMIT}
              labelWidth="w-56"
              emptyText="Nobody refused."
            />
          </UsageReportCard>
        </UsageReportSection>
      )}

      <UsageReportCard>
        <UsageReportNotes notes={MCP_REPORT_NOTES} />
      </UsageReportCard>
    </UsageReportSection>
  )
}

export default UsageMcpReport
